import React, { useState, useRef, useEffect } from "react";
import { SelectProps, Option } from "./Select.types";

const Select: React.FC<SelectProps> = ({
  label,
  options,
  value,
  onChange,
  multiSelect = false,
  disabled = false,
  required = false,
  error,
  theme = "light",
  placeholder,
  style,
  dropdownBackgroundColor,
  inputBackgroundColor,
}) => {
  const isDark = theme === "dark";

  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const isSelected = (val: string) =>
    Array.isArray(value) ? value.includes(val) : value === val;

  const displayValue = () => {
    if (!value || (Array.isArray(value) && value.length === 0)) {
      return (
        placeholder || (multiSelect ? "Select Options" : "Select an Option")
      );
    }

    if (!multiSelect) {
      const selected = options.find((o) => o.value === value);
      return selected?.label || value;
    }

    // Multi-select logic
    if (Array.isArray(value)) {
      const selectedLabels = options
        .filter((o) => value.includes(o.value))
        .map((o) => o.label);

      if (selectedLabels.length === 1) {
        return selectedLabels[0];
      }

      return `${selectedLabels.length} Selected`;
    }

    return "";
  };

  const handleChange = (val: string) => {
    if (multiSelect && Array.isArray(value)) {
      if (value.includes(val)) {
        onChange(value.filter((v) => v !== val));
      } else {
        onChange([...value, val]);
      }
    } else {
      onChange(val);
      setOpen(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div
      ref={dropdownRef}
      style={{
        display: "flex",
        flexDirection: "column",
        fontFamily: "Inter, sans-serif",
        gap: 4,
        width: "140px",
        position: "relative",
        ...style, // Custom style override
      }}
    >
      {label && (
        <label style={{ fontSize: 14, fontWeight: 500 }}>
          {label}
          {required && <span style={{ color: "red" }}> *</span>}
        </label>
      )}

      <div
        style={{
          width: "100%",
          height: 40,
          border: error
            ? "1px solid red"
            : open
            ? `2px solid #5876EE`
            : `1px solid ${isDark ? "#424242" : "#ccc"}`,
          borderRadius: 4,
          backgroundColor:
            inputBackgroundColor || (isDark ? "#333333" : "#FFFFFF"),
          color: isDark ? "#FFFFFF" : "#1a1a1a",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "0 8px",
          fontSize: 14,
          lineHeight: "24px",
          cursor: disabled ? "not-allowed" : "pointer",
          position: "relative",
        }}
        onClick={() => !disabled && setOpen(!open)}
      >
        <span
          style={{
            color:
              !value || (Array.isArray(value) && value.length === 0)
                ? isDark
                  ? "#888888"
                  : "#666666"
                : isDark
                ? "#FFFFFF"
                : "#1a1a1a",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {displayValue()}
        </span>
        <span
          style={{
            fontSize: 12,
            color: isDark ? "#888888" : "#666666",
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
            transition: "transform 0.2s ease",
          }}
        >
          ▼
        </span>
      </div>

      {open && (
        <div
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            marginTop: 4,
            width: "100%",
            backgroundColor:
              dropdownBackgroundColor || (isDark ? "#1D1B1D" : "#FFFFFF"),
            border: `1px solid ${isDark ? "#424242" : "#E0E0E0"}`,
            borderRadius: 4,
            display: "flex",
            flexDirection: "column",
            boxSizing: "border-box",
            zIndex: 10000,
            boxShadow: isDark
              ? "0 4px 12px rgba(0,0,0,0.3)"
              : "0 4px 12px rgba(0,0,0,0.1)",
            maxHeight: "200px",
            overflowY: "auto",
          }}
        >
          {/* ✅ Sadece Options - Search yok */}
          {options.map((option) => (
            <label
              key={option.value}
              style={{
                display: "flex",
                alignItems: "center",
                gap: multiSelect ? 10 : 0,
                padding: "8px 12px",
                minHeight: 32,
                fontSize: 14,
                lineHeight: "24px",
                color: isDark ? "#FFFFFF" : "#1a1a1a",
                cursor: "pointer",
                backgroundColor: isSelected(option.value)
                  ? isDark
                    ? "#5876EE20"
                    : "#5876EE10"
                  : "transparent",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = isDark
                  ? "#424242"
                  : "#F5F5F5";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = isSelected(option.value)
                  ? isDark
                    ? "#5876EE20"
                    : "#5876EE10"
                  : "transparent";
              }}
              onClick={(e) => {
                e.stopPropagation();
                handleChange(option.value);
              }}
            >
              {multiSelect && (
                <input
                  type="checkbox"
                  checked={isSelected(option.value)}
                  onChange={() => handleChange(option.value)}
                  style={{
                    width: 16,
                    height: 16,
                    borderRadius: 4,
                    accentColor: "#5876EE",
                  }}
                />
              )}
              {option.label}
            </label>
          ))}
        </div>
      )}

      {error && <span style={{ color: "red", fontSize: 12 }}>{error}</span>}
    </div>
  );
};

export default Select;
